import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../common/Icons';
import { formatActPrice } from './actPricingUtils';

function MetaChips({ text }) {
  if (!text) return null;
  const parts = text.split(' · ').filter(Boolean);
  return (
    <div className="ap-pricing-row__chips">
      {parts.map((part) => (
        <span key={part} className="ap-pricing-row__chip">
          {part}
        </span>
      ))}
    </div>
  );
}

function SaveButton({ isSaving, onSave, label }) {
  return (
    <button
      type="button"
      className="ap-pricing-price__save"
      disabled={isSaving}
      onClick={onSave}
      title="Enregistrer"
      aria-label={`Enregistrer le prix pour ${label}`}
    >
      {isSaving ? (
        <span className="ap-pricing-price__spin" aria-hidden />
      ) : (
        <Icon name="check" size={15} />
      )}
    </button>
  );
}

function PriceField({
  isPriced,
  price,
  onPriceChange,
  row,
  dirty,
  isSaving,
  onSave,
  label
}) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && dirty && !isSaving) {
      e.preventDefault();
      onSave();
    }
  };

  if (!isPriced) {
    if (dirty) {
      return (
        <div className="ap-pricing-price is-editing">
          <div className="ap-pricing-price__row">
            <span className="ap-pricing-price__off-label">Sans tarif</span>
            <SaveButton isSaving={isSaving} onSave={onSave} label={label} />
          </div>
        </div>
      );
    }
    return <span className="ap-pricing-price ap-pricing-price--na">—</span>;
  }

  const savedPrice = row.is_priced && row.price != null ? Number(row.price) : null;
  const draftPrice = price !== '' && !Number.isNaN(Number(price)) ? Number(price) : null;
  const showWas = dirty && savedPrice != null && draftPrice !== savedPrice;

  let state = 'is-editing';
  if (!dirty && savedPrice != null) state = 'is-settled';
  else if (!dirty && row.is_priced && savedPrice == null) state = 'is-empty';

  return (
    <div className={`ap-pricing-price ${state}`}>
      <div className="ap-pricing-price__row">
        <label className="ap-pricing-price__field">
          <span className="visually-hidden">Prix pour {label}</span>
          <input
            type="number"
            min={0}
            step={1}
            className="ap-pricing-price__input"
            value={price}
            onChange={(e) => onPriceChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="0"
            inputMode="numeric"
            aria-label={`Prix pour ${label}`}
          />
          <span className="ap-pricing-price__suffix" aria-hidden>
            Ar
          </span>
        </label>

        {dirty ? <SaveButton isSaving={isSaving} onSave={onSave} label={label} /> : null}
      </div>

      {showWas ? (
        <span className="ap-pricing-price__hint">était {formatActPrice(row.price)}</span>
      ) : state === 'is-empty' ? (
        <span className="ap-pricing-price__hint ap-pricing-price__hint--warn">À saisir</span>
      ) : null}
    </div>
  );
}

function PriceRow({ row, label, subLabel, onSave, savingId }) {
  const [isPriced, setIsPriced] = useState(Boolean(row.is_priced));
  const [price, setPrice] = useState(row.price != null ? String(row.price) : '');
  const isSaving = savingId === row.id;

  useEffect(() => {
    setIsPriced(Boolean(row.is_priced));
    setPrice(row.price != null ? String(row.price) : '');
  }, [row.id, row.is_priced, row.price]);

  const normalizeDraftPrice = () => {
    if (!isPriced || price === '') return null;
    const n = Number(price);
    return Number.isNaN(n) ? null : n;
  };

  const normalizeSavedPrice = () => {
    if (!row.is_priced) return null;
    if (row.price == null || row.price === '') return null;
    return Number(row.price);
  };

  const dirty =
    isPriced !== Boolean(row.is_priced) || normalizeDraftPrice() !== normalizeSavedPrice();

  const handleSave = () => {
    onSave(row.id, {
      is_priced: isPriced,
      price: isPriced && price !== '' ? Number(price) : null
    });
  };

  return (
    <tr
      className={[
        'ap-pricing-row',
        isPriced ? 'is-tariffed' : 'is-unpriced',
        dirty ? 'is-dirty' : '',
        isSaving ? 'is-saving' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <td className="ap-pricing-row__act" data-label="Acte">
        <div className="ap-pricing-row__act-inner">
          <span className="ap-pricing-row__name">{label}</span>
          <MetaChips text={subLabel} />
        </div>
      </td>

      <td className="ap-pricing-row__status" data-label="Tarification">
        <label className="ap-pricing-switch" title={isPriced ? 'Acte tarifé' : 'Sans tarif'}>
          <input
            type="checkbox"
            className="ap-pricing-switch__input"
            checked={isPriced}
            onChange={(e) => {
              setIsPriced(e.target.checked);
              if (!e.target.checked) setPrice('');
            }}
            aria-label={`Tarifer ${label}`}
          />
          <span className="ap-pricing-switch__track" aria-hidden />
        </label>
      </td>

      <td className="ap-pricing-row__price" data-label="Prix">
        <PriceField
          isPriced={isPriced}
          price={price}
          onPriceChange={setPrice}
          row={row}
          dirty={dirty}
          isSaving={isSaving}
          onSave={handleSave}
          label={label}
        />
      </td>
    </tr>
  );
}

export default function ActPricingTable({
  rows = [],
  initialLoading = false,
  refreshing = false,
  emptyMessage = 'Aucun acte',
  emptyFilteredMessage = 'Aucun résultat pour ces filtres.',
  hasActiveFilters = false,
  onClearFilters,
  clearFiltersLabel = 'Réinitialiser les filtres',
  getLabel = (row) => row.name || '—',
  getSubLabel = () => null,
  onSave
}) {
  const [savingId, setSavingId] = useState(null);

  const handleSave = useCallback(
    async (id, payload) => {
      setSavingId(id);
      try {
        await onSave(id, payload);
      } finally {
        setSavingId(null);
      }
    },
    [onSave]
  );

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => String(getLabel(a)).localeCompare(String(getLabel(b)), 'fr')),
    [rows, getLabel]
  );

  if (initialLoading && !sortedRows.length) {
    return (
      <div className="ap-pricing-state" role="status">
        <span className="ap-pricing-price__spin ap-pricing-price__spin--lg" aria-hidden />
        <p>Chargement des actes…</p>
      </div>
    );
  }

  if (!sortedRows.length) {
    const isFiltered = hasActiveFilters;
    return (
      <div className="ap-pricing-state ap-pricing-state--empty">
        <div className="ap-pricing-state__icon" aria-hidden>
          <Icon name={isFiltered ? 'search' : 'inbox'} size={36} />
        </div>
        <h3 className="ap-pricing-state__title">
          {isFiltered ? 'Aucun résultat' : 'Aucun acte'}
        </h3>
        <p className="ap-pricing-state__text">
          {isFiltered ? emptyFilteredMessage : emptyMessage}
        </p>
        {isFiltered && onClearFilters ? (
          <button type="button" className="ap-pricing-clear-btn" onClick={onClearFilters}>
            {clearFiltersLabel}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`ap-pricing-table-wrap${refreshing ? ' is-refreshing' : ''}`}
      aria-busy={refreshing}
    >
      <table className="ap-pricing-table">
        <thead>
          <tr>
            <th scope="col">Acte</th>
            <th scope="col">Tarification</th>
            <th scope="col" className="ap-pricing-table__price-col">
              Prix
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <PriceRow
              key={row.id}
              row={row}
              label={getLabel(row)}
              subLabel={getSubLabel(row)}
              onSave={handleSave}
              savingId={savingId}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
